import React, {MouseEventHandler, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Button,
  Horizontal,
  Icon,
  KeyEnterIcon,
  KeyShiftIcon,
  LoadingSpinnerIcon,
  PlusIcon,
  PopOut,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import {AssetService} from '@taopix/taopix-core/dist/types/types';
import useKeyboardMode from '../../Hooks/useKeyboardMode';
import {PopOutProps} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import {useRegisterKeyboardCommand} from '../Context/KeyboardCommandsContext';

export type AssetServiceDialogAddPicsButtonProps = {
  addSelectedPictures: MouseEventHandler;
  selectedPicsCount: number;
  loading: boolean;
  service?: AssetService | null;
};

const AssetServiceDialogAddPicsButton = ({
  addSelectedPictures,
  selectedPicsCount,
  loading,
  service,
  ...props
}: AssetServiceDialogAddPicsButtonProps) => {
  const {t} = useTranslation(['picturemanager']);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const keyboardMode = useKeyboardMode();

  const clickButton = (e: React.MouseEvent<Element, MouseEvent>) => {
    addSelectedPictures(e);
  };

  useRegisterKeyboardCommand({
    code: 'Enter',
    shiftKey: true,
    callback: () => {
      buttonRef?.current?.click();
    },
  });

  const popOutProps: Partial<PopOutProps> = {
    arrow: true,
    anchorId: 'asset-service-dialog-add-pics-button',
    className: 'flex flex-col p-xs max-w-[300rem]',
    anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Centre},
    transformOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Centre},
    popDirection: 'up',
    offset: {x: 0, y: -10},
    onClose: () => {},
    excludeIdFromClickOutside: ['asset-service-dialog-add-pics-button'],
    theme: 'tpx-theme-workarea',
    themeTip: 'Workarea',
    contentTheme: ThemeName.Tip,
    repositionOnResize: 'tpx-asset-services-dialog',
  };

  return service?.code && service?.code !== '%UPLOADFROMDEVICE%' ? (
    <>
      <Button
        id={'asset-service-dialog-add-pics-button'}
        ref={buttonRef}
        disabled={selectedPicsCount === 0 || loading}
        buttonStyle="primary"
        label={
          selectedPicsCount === 0
            ? t('str_ButtonAddSelectedPictures', {
                ns: 'picturemanager',
              }).replace('^0', String(selectedPicsCount))
            : t('str_ButtonAddNumberSelectedPictures', {
                ns: 'picturemanager',
              }).replace('^0', String(selectedPicsCount))
        }
        onClick={e => clickButton(e)}
        startIcon={loading ? <LoadingSpinnerIcon /> : <PlusIcon />}
      />
      <PopOut
        id={'asset-service-dialog-add-pics-keyboard-help'}
        open={keyboardMode && selectedPicsCount > 0}
        {...popOutProps}
      >
        <span className={'flex items-center'}>
          <Icon icon={<KeyShiftIcon />} size="large" title="Icon for key 'Shift'" />
          +
          <Icon icon={<KeyEnterIcon />} size="large" title="Icon for key 'Enter'" />
        </span>
      </PopOut>
    </>
  ) : (
    <></>
  );
};

export default AssetServiceDialogAddPicsButton;
